import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BadgePercent,
  Gift,
  Landmark,
  MapPin,
  RotateCcw,
  ShoppingBag,
  Star,
  Store,
  Tag,
  Timer,
  Truck,
  Zap,
} from 'lucide-react';
import ProductCard, { ProductCardSkeleton } from '@/components/shop/ProductCard';
import { CategoryGlyph } from '@/components/shop/ShopChrome';
import { Img, ScrollRow, Shimmer } from '@/components/ui';
import { categoryLook } from '@/lib/shopCategories';
import { rupees } from '@/lib/shop';
import { useCountdown, useSectionTracking } from '@/lib/shopHome';
import { SectionHeader, Shelf, SmartLink, initials, toneFor } from '@/components/shop/storefront/common';

const RAIL_ITEM = 'w-[44vw] max-w-[220px] sm:w-[200px]';

/** Product cards in a rail, each tagged for view/click stats by slug. */
const TrackedRail = ({ items, badgeMin }) => (
  <ScrollRow itemClassName={RAIL_ITEM}>
    {items.map((p) => (
      <div key={p.id ?? p.slug} data-item-key={p.slug} className="h-full">
        <ProductCard product={p} badgeMin={badgeMin} />
      </div>
    ))}
  </ScrollRow>
);

/* ------------------------------------------------------- offer strip */

const OFFER_ICONS = {
  bank: { Icon: Landmark, cls: 'bg-indigo-50 text-indigo-600' },
  truck: { Icon: Truck, cls: 'bg-emerald-50 text-emerald-600' },
  return: { Icon: RotateCcw, cls: 'bg-sky-50 text-sky-600' },
  tag: { Icon: Tag, cls: 'bg-rose-50 text-rose-600' },
  star: { Icon: Star, cls: 'bg-amber-50 text-amber-600' },
  store: { Icon: Store, cls: 'bg-violet-50 text-violet-600' },
  gift: { Icon: Gift, cls: 'bg-pink-50 text-pink-600' },
  bolt: { Icon: Zap, cls: 'bg-orange-50 text-orange-600' },
};

export const OfferStrip = ({ section }) => {
  const chips = section.data?.chips || [];
  const track = useSectionTracking(section.id, [chips]);
  if (!chips.length) return null;
  return (
    <section className="lk-container" aria-label={section.title || 'Offers'} {...track}>
      <ul className="scrollbar-hide -mx-4 flex snap-x gap-2 overflow-x-auto scroll-px-4 px-4 py-0.5 sm:mx-0 sm:flex-wrap sm:px-0 lg:justify-center">
        {chips.map((c) => {
          const { Icon, cls } = OFFER_ICONS[c.icon] || OFFER_ICONS.tag;
          const body = (
            <>
              <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${cls}`}>
                <Icon className="h-4 w-4" aria-hidden="true" />
              </span>
              <span className="whitespace-nowrap">{c.text}</span>
              {c.link && <ArrowRight className="h-3.5 w-3.5 text-ink-400" aria-hidden="true" />}
            </>
          );
          const chipCls =
            'flex min-h-[40px] items-center gap-2 rounded-full border border-ink-200/70 bg-white py-1.5 pl-1.5 pr-3.5 text-[13px] font-medium text-ink-700 shadow-[0_1px_2px_rgba(16,24,40,.04)]';
          return (
            <li key={c.key} className="shrink-0 snap-start" data-item-key={c.key}>
              {c.link ? (
                <SmartLink link={c.link} className={`lk-focus ${chipCls} transition hover:border-ink-400 hover:text-ink-900`}>
                  {body}
                </SmartLink>
              ) : (
                <span className={chipCls}>{body}</span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
};

/* -------------------------------------------------------- categories */

export const CategoryTiles = ({ section, categories, loading }) => {
  const track = useSectionTracking(section?.id, [categories]);
  if (!loading && !categories?.length) return null;
  return (
    <Shelf innerClassName="py-4">
      <section aria-label={section?.title || 'Shop by category'} {...track}>
        {section?.title && <SectionHeader title={section.title} subtitle={section.subtitle} />}
        <ul className="scrollbar-hide -mx-4 flex snap-x gap-1 overflow-x-auto scroll-px-4 px-4 sm:mx-0 sm:grid sm:grid-cols-[repeat(auto-fit,minmax(112px,1fr))] sm:gap-3 sm:overflow-visible sm:px-0">
          {loading
            ? Array.from({ length: 6 }, (_, i) => (
                <li key={i} className="flex w-[78px] shrink-0 flex-col items-center gap-2 sm:w-auto">
                  <Shimmer className="h-16 w-16 rounded-2xl" />
                  <Shimmer className="h-3 w-12" />
                </li>
              ))
            : categories.map((c) => {
                const { bg } = categoryLook(c);
                return (
                  <li key={c.id} className="w-[78px] shrink-0 snap-start sm:w-auto" data-item-key={c.slug}>
                    <Link
                      to={`/shop/c/${c.slug}`}
                      className="lk-focus group flex flex-col items-center gap-1.5 rounded-2xl p-1 text-center sm:p-3 sm:transition sm:hover:bg-surface-muted"
                    >
                      <span className="rounded-[1.1rem] p-0.5 transition-transform duration-300 group-hover:-translate-y-0.5" style={{ background: `linear-gradient(160deg, #fff, ${bg})` }}>
                        <CategoryGlyph category={c} className="h-16 w-16 sm:h-[4.5rem] sm:w-[4.5rem]" iconClassName="h-8 w-8" />
                      </span>
                      <span className="line-clamp-2 text-[12px] font-semibold leading-tight text-ink-900 sm:text-sm">{c.name}</span>
                    </Link>
                  </li>
                );
              })}
        </ul>
      </section>
    </Shelf>
  );
};

/* ------------------------------------------------------- deal of day */

const pad = (n) => String(n).padStart(2, '0');

const Countdown = ({ ms }) => {
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const box = 'tabular inline-flex min-w-[2rem] justify-center rounded-md bg-white px-1.5 py-1 text-[15px] font-bold leading-none text-ink-900 shadow-sm sm:min-w-[2.4rem] sm:text-lg';
  return (
    <span className="inline-flex items-center gap-2">
      <Timer className="h-4 w-4 text-amber-300" aria-hidden="true" />
      <span className="text-[13px] font-medium text-white/85">Ends in</span>
      <span role="timer" aria-label={`Ends in ${h} hours ${m} minutes`} className="inline-flex items-center gap-1 text-white">
        <span className={box}>{pad(h)}</span>
        <span aria-hidden="true" className="font-bold">:</span>
        <span className={box}>{pad(m)}</span>
        <span aria-hidden="true" className="font-bold">:</span>
        <span className={box}>{pad(s)}</span>
      </span>
    </span>
  );
};

export const DealOfDay = ({ section }) => {
  const items = section.data?.items || [];
  const left = useCountdown(section.data?.endsAt);
  const track = useSectionTracking(section.id, [items, left === 0]);
  // A deal needs a real end time; when it passes, the deal is gone — no fake urgency.
  if (!items.length || left == null || left <= 0) return null;
  return (
    <section className="lk-container px-0 sm:px-6 lg:px-8" {...track}>
      <div
        className="relative overflow-hidden px-4 pb-3 pt-5 sm:rounded-3xl sm:px-6 sm:pt-6"
        style={{ background: 'linear-gradient(125deg, #0b1f4d 0%, #0b3f99 48%, #0a6ee0 100%)' }}
      >
        <span aria-hidden="true" className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-sky-400/25 blur-3xl" />
        <span aria-hidden="true" className="pointer-events-none absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-indigo-400/20 blur-3xl" />
        <div className="relative mb-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
          <div className="min-w-0">
            <h2 className="flex items-center gap-2 text-[1.25rem] font-bold leading-tight text-white sm:text-h2">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-300 text-ink-900">
                <Zap className="h-[18px] w-[18px] fill-current" aria-hidden="true" />
              </span>
              {section.title || 'Deals of the day'}
            </h2>
            {section.subtitle && <p className="mt-1 text-sm text-white/80">{section.subtitle}</p>}
          </div>
          <div className="flex w-full items-center justify-between gap-3 sm:w-auto sm:justify-end">
            <Countdown ms={left} />
            {section.data?.viewAllLink && (
              <SmartLink
                link={section.data.viewAllLink}
                data-item-key="view_all"
                data-track-manual=""
                className="lk-focus inline-flex min-h-[36px] shrink-0 items-center gap-1 rounded-full bg-white px-3.5 text-sm font-semibold text-ink-900 transition hover:bg-white/90"
              >
                View all <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </SmartLink>
            )}
          </div>
        </div>
        <div className="relative">
          <TrackedRail items={items} badgeMin={1} />
        </div>
      </div>
    </section>
  );
};

/* --------------------------------------------------- collection grid */

const GRID_COLS = { 1: 'lg:grid-cols-1', 2: 'lg:grid-cols-2', 3: 'lg:grid-cols-3', 4: 'lg:grid-cols-4' };

export const CollectionGrid = ({ section }) => {
  const cards = section.data?.cards || [];
  const track = useSectionTracking(section.id, [cards]);
  if (!cards.length) return null;
  return (
    <section className="lk-container" {...track}>
      <SectionHeader title={section.title} subtitle={section.subtitle} />
      <div className={`grid grid-cols-2 gap-3 sm:gap-4 ${cards.length >= 3 ? 'md:grid-cols-4' : ''} ${GRID_COLS[Math.min(4, cards.length)]}`}>
        {cards.map((card) => {
          const imgs = (card.images || []).filter(Boolean).slice(0, 4);
          // Always a square photo block: 4 → 2×2, 3 → one tall + two, 2 → two tall halves, 1 → one.
          const span = (i) => (imgs.length === 1 ? 'col-span-2 row-span-2' : imgs.length === 2 || (imgs.length === 3 && i === 0) ? 'row-span-2' : '');
          return (
            <div key={card.key} data-item-key={card.key} className="min-w-0">
              <SmartLink
                link={card.link}
                className="lk-focus group flex h-full flex-col rounded-2xl border border-ink-200/70 bg-white p-2.5 shadow-card transition hover:-translate-y-0.5 hover:shadow-cardHover sm:p-4"
              >
                <h3 className="line-clamp-1 px-0.5 text-[14px] font-bold text-ink-900 sm:text-base">{card.title}</h3>
                <div className="mt-2 grid aspect-square grid-cols-2 grid-rows-2 gap-1.5 sm:gap-2">
                  {imgs.length ? (
                    imgs.map((src, i) => (
                      <Img
                        key={i}
                        src={src}
                        alt=""
                        label={card.title}
                        className={`h-full w-full rounded-lg sm:rounded-xl ${span(i)}`}
                        imgClassName="transition-transform duration-500 group-hover:scale-[1.05]"
                      />
                    ))
                  ) : (
                    <Monogram name={card.title} className="col-span-2 row-span-2 rounded-lg sm:rounded-xl" text="text-3xl" />
                  )}
                </div>
                <div className="mt-auto flex items-end justify-between gap-2 px-0.5 pt-2.5">
                  <div className="min-w-0">
                    {card.subtitle && <p className="line-clamp-1 text-[12px] font-semibold text-success sm:text-sm">{card.subtitle}</p>}
                    <span className="mt-0.5 inline-flex items-center gap-1 text-[12px] font-semibold text-brand-secondary sm:text-sm">
                      Explore <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                    </span>
                  </div>
                </div>
              </SmartLink>
            </div>
          );
        })}
      </div>
    </section>
  );
};

/* ------------------------------------------------------- price store */

const PRICE_TONES = [
  ['#3730a3', '#7c3aed'],
  ['#9f1239', '#e11d48'],
  ['#9a3412', '#ea580c'],
  ['#065f46', '#059669'],
  ['#075985', '#0284c7'],
  ['#6b21a8', '#c026d3'],
];
const PRICE_COLS = { 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3', 4: 'sm:grid-cols-4', 5: 'sm:grid-cols-3 lg:grid-cols-5', 6: 'sm:grid-cols-3 lg:grid-cols-6' };

export const PriceStore = ({ section }) => {
  const tiers = section.data?.tiers || [];
  const track = useSectionTracking(section.id, [tiers]);
  if (!tiers.length) return null;
  return (
    <Shelf>
      <section {...track}>
        <SectionHeader title={section.title || 'Shop by price'} subtitle={section.subtitle} />
        <div className={`grid grid-cols-2 gap-3 sm:gap-4 ${PRICE_COLS[Math.min(6, tiers.length)] || 'sm:grid-cols-4'}`}>
          {tiers.map((t, i) => {
            const [from, to] = PRICE_TONES[i % PRICE_TONES.length];
            const m = /^(under|below|upto|up to)\s+(.+)$/i.exec(t.label || '');
            const lead = m ? m[1] : null;
            const amount = m ? m[2] : t.label || `Under ${rupees(t.maxPrice)}`;
            return (
              <div key={t.key} data-item-key={t.key}>
                <SmartLink
                  link={t.link}
                  className="lk-focus group relative flex aspect-[16/10] flex-col justify-between overflow-hidden rounded-2xl p-3.5 text-white shadow-card transition hover:-translate-y-0.5 hover:shadow-cardHover sm:p-5"
                  style={t.image ? undefined : { background: `linear-gradient(140deg, ${from} 0%, ${to} 100%)` }}
                >
                  {t.image ? (
                    <>
                      <Img src={t.image} alt="" className="absolute inset-0" monogram={false} imgClassName="transition-transform duration-500 group-hover:scale-105" />
                      <span aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/10" />
                    </>
                  ) : (
                    <>
                      <span aria-hidden="true" className="absolute -right-6 -top-8 h-28 w-28 rounded-full bg-white/10" />
                      <ShoppingBag aria-hidden="true" className="absolute -bottom-3 -right-2 h-20 w-20 rotate-[-12deg] text-white/15 transition-transform duration-500 group-hover:rotate-0 sm:h-24 sm:w-24" strokeWidth={1.5} />
                    </>
                  )}
                  <span className="relative">
                    {lead && <span className="block text-[11px] font-bold uppercase tracking-[0.14em] text-white/80 sm:text-xs">{lead}</span>}
                    <span className="tabular block font-display text-[1.7rem] font-extrabold leading-none tracking-tight sm:text-[2.2rem]">{amount}</span>
                  </span>
                  <span className="relative inline-flex items-center gap-1 text-[12px] font-semibold text-white/90 sm:text-sm">
                    Shop now <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                  </span>
                </SmartLink>
              </div>
            );
          })}
        </div>
      </section>
    </Shelf>
  );
};

/* ------------------------------------------------------ product rail */

export const ProductRailSection = ({ section, icon }) => {
  const items = section.data?.items || [];
  const track = useSectionTracking(section.id, [items]);
  if (!items.length) return null;
  return (
    <Shelf>
      <section {...track}>
        <SectionHeader
          title={section.title}
          subtitle={section.subtitle}
          viewAllLink={section.data?.viewAllLink}
          sponsored={section.data?.sponsored || section.sponsored}
          sponsorName={section.data?.sponsorName}
          icon={icon}
        />
        <TrackedRail items={items} />
      </section>
    </Shelf>
  );
};

export const RailSkeleton = () => (
  <Shelf>
    <Shimmer className="mb-4 h-7 w-48" />
    <ScrollRow itemClassName={RAIL_ITEM}>
      {Array.from({ length: 6 }, (_, i) => (
        <ProductCardSkeleton key={i} />
      ))}
    </ScrollRow>
  </Shelf>
);

/* --------------------------------------------------- brand spotlight */

const Monogram = ({ name, className = '', text = 'text-2xl' }) => {
  const [from, to] = toneFor(name);
  return (
    <span
      aria-hidden="true"
      className={`flex items-center justify-center font-display font-extrabold tracking-tight text-white ${text} ${className}`}
      style={{ background: `radial-gradient(120% 90% at 20% 10%, rgba(255,255,255,.25), transparent 55%), linear-gradient(150deg, ${from}, ${to})` }}
    >
      {initials(name)}
    </span>
  );
};

export const BrandSpotlight = ({ section }) => {
  const brands = section.data?.brands || [];
  const track = useSectionTracking(section.id, [brands]);
  if (!brands.length) return null;
  return (
    <Shelf>
      <section {...track}>
        <SectionHeader title={section.title || 'Top brands'} subtitle={section.subtitle} sponsored={section.data?.sponsored || section.sponsored} sponsorName={section.data?.sponsorName} />
        <ScrollRow itemClassName="w-[40vw] max-w-[200px] sm:w-[190px]">
          {brands.map((b) => (
            <div key={b.slug} data-item-key={b.slug} className="h-full">
              <SmartLink
                link={b.link}
                className="lk-focus group flex h-full flex-col overflow-hidden rounded-2xl border border-ink-200/70 bg-white transition hover:-translate-y-0.5 hover:shadow-cardHover"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-surface-muted">
                  {b.logo ? (
                    <div className="flex h-full w-full items-center justify-center bg-white p-5">
                      <img src={b.logo} alt="" loading="lazy" className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-105" />
                    </div>
                  ) : (
                    <Monogram name={b.name} className="h-full w-full transition-transform duration-500 group-hover:scale-105" text="text-[2.4rem]" />
                  )}
                  {b.maxDiscount > 0 && (
                    <span className="absolute bottom-2 left-2 rounded-md bg-success px-1.5 py-0.5 text-[11px] font-bold text-white shadow-sm">Up to {b.maxDiscount}% off</span>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-3">
                  <h3 className="truncate text-sm font-semibold text-ink-900">{b.name}</h3>
                  {b.fromPrice > 0 && (
                    <p className="mt-0.5 text-[13px] text-ink-500">
                      From <span className="tabular font-bold text-ink-900">{rupees(b.fromPrice)}</span>
                    </p>
                  )}
                  {b.products > 0 && <p className="mt-auto pt-1 text-[11px] text-ink-500">{b.products} product{b.products === 1 ? '' : 's'}</p>}
                </div>
              </SmartLink>
            </div>
          ))}
        </ScrollRow>
      </section>
    </Shelf>
  );
};

/* --------------------------------------------------- store spotlight */

export const StoreSpotlight = ({ section }) => {
  const stores = section.data?.stores || [];
  const track = useSectionTracking(section.id, [stores]);
  if (!stores.length) return null;
  return (
    <Shelf>
      <section {...track}>
        <SectionHeader title={section.title || 'Shops from your city'} subtitle={section.subtitle} />
        <ScrollRow itemClassName="w-[76vw] max-w-[300px] sm:w-[290px]">
          {stores.map((st) => {
            const [from, to] = toneFor(st.name);
            return (
              <div key={st.slug} data-item-key={st.slug} className="h-full">
                <SmartLink
                  link={st.link}
                  className="lk-focus group flex h-full flex-col overflow-hidden rounded-2xl border border-ink-200/70 bg-white transition hover:-translate-y-0.5 hover:shadow-cardHover"
                >
                  <div className="relative aspect-[16/7] overflow-hidden" style={st.banner ? undefined : { background: `linear-gradient(135deg, ${from}, ${to})` }}>
                    {st.banner ? (
                      <Img src={st.banner} alt="" className="absolute inset-0" monogram={false} imgClassName="transition-transform duration-500 group-hover:scale-105" />
                    ) : (
                      <Store aria-hidden="true" className="absolute -bottom-4 right-3 h-24 w-24 text-white/15" strokeWidth={1.25} />
                    )}
                  </div>
                  <div className="relative flex flex-1 flex-col px-4 pb-4">
                    <span className="-mt-7 mb-2 block h-14 w-14 overflow-hidden rounded-2xl bg-white shadow-card ring-4 ring-white">
                      {st.logo ? <Img src={st.logo} alt="" label={st.name} className="h-full w-full" /> : <Monogram name={st.name} className="h-full w-full" text="text-lg" />}
                    </span>
                    <h3 className="truncate text-[15px] font-semibold text-ink-900">{st.name}</h3>
                    <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs text-ink-500">
                      {st.rating > 0 ? (
                        <span className="tabular inline-flex items-center gap-0.5 rounded-md bg-success px-1.5 py-0.5 font-bold text-white">
                          {Number(st.rating).toFixed(1)} <Star className="h-3 w-3 fill-white" aria-hidden="true" />
                          {st.ratingCount > 0 && <span className="sr-only"> from {st.ratingCount} ratings</span>}
                        </span>
                      ) : (
                        <span className="rounded-md bg-brand-tint px-1.5 py-0.5 font-semibold text-brand-secondary">New on LK</span>
                      )}
                      {st.ratingCount > 0 && <span className="tabular">({st.ratingCount.toLocaleString('en-IN')})</span>}
                      {st.city && (
                        <span className="inline-flex items-center gap-0.5">
                          <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                          {st.city}
                        </span>
                      )}
                    </div>
                    {st.about && <p className="mt-2 line-clamp-2 text-[13px] text-ink-500">{st.about}</p>}
                    <div className="mt-auto flex items-center justify-between gap-2 pt-3">
                      <span className="text-xs font-medium text-ink-500">
                        {st.products} product{st.products === 1 ? '' : 's'}
                      </span>
                      <span className="inline-flex items-center gap-1 text-sm font-semibold text-brand-secondary">
                        Visit store <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                      </span>
                    </div>
                  </div>
                </SmartLink>
              </div>
            );
          })}
        </ScrollRow>
      </section>
    </Shelf>
  );
};

/* ------------------------------------------------------ personal rows */

const MiniCard = ({ item }) => (
  <Link to={item.to} className="lk-focus group flex h-full flex-col">
    <Img src={item.image} alt="" label={item.title} ratio="1 / 1" className="rounded-xl border border-ink-200/70" imgClassName="transition-transform duration-500 group-hover:scale-[1.05]" />
    <p className="mt-2 line-clamp-2 text-[12px] leading-4 text-ink-700 group-hover:text-ink-900">{item.title}</p>
    {item.price != null && (
      <p className="tabular mt-1 flex flex-wrap items-baseline gap-x-1.5 text-[13px]">
        <span className="font-bold text-ink-900">{rupees(item.price)}</span>
        {item.discountPercent > 0 && <span className="text-[11px] font-semibold text-success">{item.discountPercent}% off</span>}
      </p>
    )}
    {item.note && <p className="mt-0.5 text-[11px] text-ink-500">{item.note}</p>}
  </Link>
);

export const PersonalRail = ({ title, subtitle, items, viewAll, icon }) => {
  if (!items?.length) return null;
  return (
    <Shelf>
      <section>
        <SectionHeader title={title} subtitle={subtitle} viewAllLink={viewAll?.link} viewAllLabel={viewAll?.label} icon={icon} />
        <ScrollRow itemClassName="w-[31vw] max-w-[150px] sm:w-[150px]">
          {items.map((it) => (
            <MiniCard key={it.key} item={it} />
          ))}
        </ScrollRow>
      </section>
    </Shelf>
  );
};

export const SECTION_ICONS = { deals: BadgePercent };
